import { useCallback, useMemo, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useToast } from "../contexts/ToastContext";
import { api } from "../lib/api";
import { ReceiptUploadOut } from "../lib/schemas";
import { webApp, useBackButton, useMainButton } from "../lib/telegram";

export default function Receipt() {
  const { id } = useParams<{ id: string }>();
  const groupId = Number(id);
  const nav = useNavigate();
  const showToast = useToast();

  const [photo, setPhoto] = useState<File | null>(null);
  const [description, setDescription] = useState("");
  const [uploading, setUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const cameraInputRef = useRef<HTMLInputElement>(null);
  const galleryInputRef = useRef<HTMLInputElement>(null);

  const photoUrl = useMemo(
    () => (photo ? URL.createObjectURL(photo) : null),
    [photo],
  );
  useEffect(() => {
    return () => { if (photoUrl) URL.revokeObjectURL(photoUrl); };
  }, [photoUrl]);

  const canUpload = photo !== null && description.trim().length > 0 && !uploading;

  async function handleUpload() {
    if (!canUpload) return;
    setUploading(true);
    setUploadError(null);
    webApp.MainButton.showProgress(false);
    try {
      const form = new FormData();
      form.append("photo", photo!);
      form.append("description", description.trim());
      const raw = await api<unknown>("POST", `/chats/${groupId}/receipts`, { form });
      ReceiptUploadOut.parse(raw);
      showToast("Receipt is processing — it'll appear below to split in a moment.", { type: "success" });
      nav(`/groups/${groupId}`, { replace: true });
    } catch (e) {
      setUploadError(e instanceof Error ? e.message : "Upload failed");
    } finally {
      setUploading(false);
      webApp.MainButton.hideProgress();
    }
  }

  const handleBack = useCallback(() => nav(`/groups/${groupId}`), [groupId, nav]);
  useBackButton(handleBack);

  const uploadClickRef = useRef(handleUpload);
  uploadClickRef.current = handleUpload;

  useMainButton("Upload", canUpload, useCallback(() => { void uploadClickRef.current(); }, []));

  return (
      <div className="min-h-screen bg-bg px-4 pt-6 pb-32">
        <h1 className="font-sans text-base font-bold text-fg mb-6 text-center">
          Scan receipt
        </h1>

        <div className="mb-6">
          <label className="block font-sans text-xs font-medium text-muted mb-3">
            Receipt photo
          </label>
          <div className="w-full bg-surface border border-dashed border-hairline rounded-2xl overflow-hidden">
            {photo && photoUrl ? (
              <div className="flex flex-col items-center py-8 gap-2">
                <img
                  src={photoUrl}
                  alt="Receipt preview"
                  className="max-h-40 object-contain rounded-xl"
                />
                <p className="text-muted text-xs mt-1">{photo.name}</p>
                <button
                  type="button"
                  onClick={() => setPhoto(null)}
                  className="font-sans text-xs font-semibold text-fg border border-hairline rounded-lg px-3 py-1.5 active:opacity-70 transition-opacity mt-1"
                >
                  Replace photo
                </button>
              </div>
            ) : (
              <div className="flex flex-col items-center py-6 gap-3">
                <p className="font-sans text-xs font-medium text-muted">Add a receipt photo</p>
                <div className="flex gap-2.5 w-full px-3">
                  <button
                    type="button"
                    onClick={() => cameraInputRef.current?.click()}
                    className="flex-1 flex flex-col items-center gap-2 py-5 bg-surface-2 rounded-xl active:opacity-70 transition-opacity"
                  >
                    <span className="text-3xl">📷</span>
                    <span className="font-sans text-xs font-medium text-muted">Camera</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => galleryInputRef.current?.click()}
                    className="flex-1 flex flex-col items-center gap-2 py-5 bg-surface-2 rounded-xl active:opacity-70 transition-opacity"
                  >
                    <span className="text-3xl">🖼️</span>
                    <span className="font-sans text-xs font-medium text-muted">Gallery</span>
                  </button>
                </div>
              </div>
            )}
          </div>
          <input
            ref={cameraInputRef}
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              if (f) setPhoto(f);
              e.target.value = "";
            }}
          />
          <input
            ref={galleryInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0] ?? null;
              if (f) setPhoto(f);
              e.target.value = "";
            }}
          />
        </div>

        <div className="mb-6">
          <label className="block font-sans text-xs font-medium text-muted mb-3">
            Description
          </label>
          <input
            type="text"
            placeholder="e.g. Dinner at restaurant"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            className="w-full px-0 py-3 bg-transparent text-fg border-b border-hairline focus:outline-none focus:border-fg placeholder:text-faint"
          />
        </div>

        {uploadError && (
          <p className="text-danger text-sm text-center mt-2">{uploadError}</p>
        )}
      </div>
  );
}
